import ClearIcon from '@mui/icons-material/Clear';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import React, { useMemo, useState } from 'react';

import { usePatients } from '../../api/index.js';
import { Loading, PatientsList } from '../../components/index.js';
import { type Patient } from '../../models/index.js';

const Patients = () => {
  const [searchValue, setSearchValue] = useState('');
  const [activeFilter, setActiveFilter] = useState<boolean>(true);
  const [inMonitoringHoursFilter, setInMonitoringHoursFilter] = useState<boolean>(false);
  const patients = usePatients({
    includeInactive: true,
    includeCameras: false,
    includeMatched: false,
  });

  const filteredPatients: Patient[] = useMemo(
    () =>
      patients?.data?.filter(
        ({ firstName, lastName, active, inMonitoringHours }) =>
          `${firstName} ${lastName}`.toLowerCase().includes(searchValue) &&
          (activeFilter ? active : true) &&
          (inMonitoringHoursFilter ? inMonitoringHours : true),
      ) || [],
    [patients.data, searchValue, activeFilter, inMonitoringHoursFilter],
  );

  if (patients.isLoading) {
    return <Loading />;
  }
  if (patients.isError) {
    return <h2>Error Loading Patients</h2>;
  }

  return (
    <div>
      <Typography variant={'h6'}>Please select a patient to view more details.</Typography>
      <Stack spacing={3} direction={'row'} alignItems="baseline">
        <FormControl sx={{ mt: 1, mb: 1, width: 360 }} variant="outlined">
          <InputLabel
            htmlFor="outlined-search-field"
            variant={'outlined'}
            size={'small'}
            sx={{
              '&.MuiInputLabel-shrink': {
                transform: 'translate(14px, -5px) scale(0.75)',
              },
            }}
          >
            Search
          </InputLabel>
          <OutlinedInput
            value={searchValue}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setSearchValue(event.target.value);
            }}
            size="small"
            label="Search"
            id="outlined-search-field"
            aria-describedby="outlined-search-helper-text"
            inputProps={{
              'aria-label': 'Type to search for a patient.',
            }}
            endAdornment={
              searchValue ? (
                <InputAdornment position="end">
                  <IconButton aria-label="clear search field" onClick={() => setSearchValue('')} edge="end">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ) : null
            }
          />
          <FormHelperText id="outlined-search-helper-text">Type to search for a patient.</FormHelperText>
        </FormControl>
        <FormControlLabel
          control={
            <Checkbox
              checked={activeFilter}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setActiveFilter(event.target.checked)}
            />
          }
          label="Only active"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={inMonitoringHoursFilter}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setInMonitoringHoursFilter(event.target.checked)
              }
            />
          }
          label="In Monitoring hours"
        />
      </Stack>
      {filteredPatients.length === 0 ? (
        <Typography sx={{ mt: 2 }}>No patients found with the current filtering.</Typography>
      ) : null}
      <PatientsList patients={filteredPatients} searchValue={searchValue} />
    </div>
  );
};

export default Patients;
